import { catchApiError, convertFeatureToWkt, defineComponent, FieldsMap, FieldViewModel, ICropping, IOmniaShell, LocaleString, map } from "../omnia"
import { ICroppingSplitModel, IFieldsEditService, IFieldsService } from "../types"
import { SplitCmd } from "./splitCmd"
import { validateSplit } from "./validation"

import IEditContext = omnia.map.edit.IEditContext
import IEditZone = omnia.map.edit.IEditZone
import EditMode = omnia.map.edit.EditMode
import IMapMeasuringService = omnia.map.IMapMeasuringService
import { ol_Feature } from "@omnia/exports/openlayers"

export class SplitCtrl extends omnia.shell.Component {

    static create(): ng.IDirective {

        return defineComponent(SplitCtrl, "app/fields/split/split.html", {
            fieldsMap: "="
        })

    }

    public fieldsMap: FieldsMap

    private _mapSplitContext: MapSplitContext

    public cropping: FieldViewModel
    hasBoundary: boolean

    public splits: SplitViewModel[]

    form: ng.IFormController
    showValidationErrors: boolean

    static $inject = ["$scope", "OmniaShell", "OmniaFieldsService", "OmniaFieldsEditService", "OmniaMapMeasuringService"]

    constructor($scope: ng.IScope, public readonly shell: IOmniaShell, private svc: IFieldsService, private editSvc: IFieldsEditService, public readonly measure: IMapMeasuringService) {

        super($scope, ["vm.fieldsMap"])

        this._icon = "icon-split"
        this._title = "Split a crop" as LocaleString
    }

    protected setup() {

        const splitCmd = <SplitCmd>this.execArguments

        if (!splitCmd) {
            this.cropping = null
            return
        }

        this.setupCropping(splitCmd.cropping)

        if (this.hasBoundary) {
            this.splits = []
        }
        else {
            this.splits = this.getDefaultSplits()
        }

        if (this.hasBoundary) {
            this._mapSplitContext = new MapSplitContext(this)
            this.svc.setView(true)
        }
        else {
            this.svc.setView(false)
        }

        this.editSvc.startEditing(true, this.cropping.cropping)

    }

    cancel() {

        if (this._mapSplitContext) {
            this._mapSplitContext.dispose()
        }

        this.editSvc.stopEditing()

        super.cancel()

    }

    private setupCropping(cropping: FieldViewModel) {
        this.cropping = cropping
        this.hasBoundary = this.cropping.hasBoundary()
    }

    get target(): ICropping {
        return this.cropping ? this.cropping.cropping : null
    }

    get inPlan(): boolean {
        return this.cropping && this.cropping.isUsedInPlans()
    }

    get mapSplit(): boolean {
        return this.hasBoundary
    }

    get mapSplitContext(): MapSplitContext {
        return this._mapSplitContext
    }

    get showSplits(): boolean {
        if (!this.mapSplitContext) return true
        return (this.mapSplitContext.editMode != EditMode.Merge)
    }

    hasEnoughSplits(): boolean {
        return this.splits && (this.splits.length > 1)
    }

    isEditingMap(): boolean {
        return this._mapSplitContext && (!!this._mapSplitContext.editMode)
    }

    private getDefaultSplits(): SplitViewModel[] {

        var area = this.cropping.cropping.Area

        return [

            new SplitViewModel(this, true, area / 2),
            new SplitViewModel(this, false, area / 2)

        ]

    }


    removeSplit(split: SplitViewModel) {

        this.splits.remove(s => s == split)

    }

    addSplit(): SplitViewModel {

        const split = new SplitViewModel(this, this.splits.length == 0)

        this.splits.push(split)

        return split

    }

    canCommit(): boolean {
        if (!this.hasEnoughSplits()) return false
        return this.form && this.form.$valid
    }

    commit() {

        if (!this.canCommit()) {
            this.showValidationErrors = this.form.$invalid
            return
        }

        this.showValidationErrors = false

        validateSplit(this.cropping, this.shell, this.svc).then(ok => {

            if (ok) {

                const model: ICroppingSplitModel = {

                    HoldingId: this.svc.holdingId,
                    YearId: this.cropping.yearId,

                    FieldId: this.cropping.fieldId,
                    CroppingId: this.cropping.croppingId,

                    DeleteLayers: ok == "delete",

                    Splits: this.splits.map(s => {

                        return {
                            RetainCroppingInfo: s.retainCroppingInfo,
                            Area: s.area,
                            BoundaryWKT: convertFeatureToWkt(s.feature)
                        }

                    })

                }

                catchApiError(this.shell, () =>

                    this.svc.splitCropping(model).then(() => this.cancel())

                )

            }

        })
    }

}

export class SplitViewModel implements IEditZone {

    private _area: number

    constructor(private ctrl: SplitCtrl, public retainCroppingInfo: boolean, area?: number) {
        this._area = area
    }

    feature: ol_Feature
    value: any

    get area(): number {

        if (this.feature) {
            var a = this.ctrl.measure.getArea(this.feature)
            return Math.round(a / 100) / 100
        }

        return this._area

    }

    set area(value: number) {
        this._area = value
    }

    getDescription(): string {
        var index = this.ctrl.splits.indexOf(this) + 1
        return "Split #" + index + " (" + this.area + " ha)"
    }

}


export class MapSplitContext implements IEditContext {

    outerGeometry: any

    map: FieldsMap
    layer: MapSplitLayer

    zones: SplitViewModel[] = []

    editMode: EditMode

    enablePolygonDrawingTools = true


    constructor(private ctrl: SplitCtrl) {
        this.setup()
    }


    private setup() {

        this.map = this.ctrl.fieldsMap

        var croppingFeature = this.map.findFeatureWithData(this.ctrl.cropping)

        if (!croppingFeature) {
            throw new Error("Split: no feature found for the target cropping")
        }

        this.outerGeometry = croppingFeature.getGeometry()

        this.map.croppingLayer.isVisible = true

        this.layer = new MapSplitLayer()
        this.map.addLayer(this.layer, true)

        this.createInitialZone()

        this.map.viewExtent(this.outerGeometry)
        this.map.croppingLayer.setHighlightFeatures([croppingFeature])

        this.map.setSelectableLayers([this.layer])

        this.map.synchronizeSelectionFromMap = false

    }

    dispose() {

        this.map.removeLayer(this.layer)

        this.map.clearSelection()

        this.map.croppingLayer.clearHighlight()

        this.map.synchronizeSelectionFromMap = true

        this.map.resetDefaultLayers(true)

    }


    private createInitialZone() {

        this.createNewZoneWithGeometry(this.outerGeometry)

    }


    createNewZoneWithGeometry(geometry: any): IEditZone {

        var initial = (this.zones.length == 0)

        var split = new SplitViewModel(this.ctrl, initial)

        this.layer.addNewZone(split, geometry)

        this.ctrl.splits.push(split)
        this.zones.push(split)

        return split

    }

    removeZone(zone: IEditZone) {

        this.map.removeFeatures([zone.feature])
        this.zones.remove(z => z == zone)
        this.ctrl.splits.remove(s => s == zone)

    }

    onEditModeChanged(mode: EditMode) {
        this.editMode = mode
    }


}

export class MapSplitLayer extends omnia.map.Layer {

    addNewZone(split: SplitViewModel, geometry: any): void {

        var feature = super.createNewFeature(geometry, split)

        split.feature = feature

    }

}
